"use client";

import { useSyncExternalStore } from "react";
import { useReducedMotion } from "motion/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Dialog, MediaPlayer } from "@marketing-sites/ui/primitives";

export interface Fin4Video {
  /** Wistia media ID of the full video the dialog plays. */
  wistiaId: string;
  /** Wistia media ID of the muted loop shown in the thumbnail. */
  previewWistiaId: string;
}

const wistia = (id: string) =>
  `https://fast.wistia.com/embed/medias/${id}.m3u8`;

const VIDEO_ID = "fin4-announcement";

const subscribeNever = () => () => {};

export function Fin4PromoVideo({
  video,
  captions,
}: {
  video: Fin4Video;
  captions?: string;
}) {
  const { t } = useLingui();
  const title = t`Introducing Fin 4`;
  const previewUrl = wistia(video.previewWistiaId);
  const reducedMotion = useReducedMotion();
  // Reduced motion is client-only; SSR and the first client render must agree.
  const hydrated = useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );

  return (
    <Dialog.Root
      wrapperClassName="shrink-0"
      openMetadata={{ analyticsId: "modal_home_fin4_video_opened" }}
      closeMetadata={{ analyticsId: "modal_home_fin4_video_closed" }}
    >
      <Dialog.Trigger
        aria-label={t`Play the Introducing Fin 4 video`}
        className="bg-off-black aspect-180/95 md:w-45 group relative flex w-36 cursor-pointer items-center justify-center overflow-hidden"
      >
        {hydrated && !reducedMotion && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 isolate"
          >
            <MediaPlayer.Context
              video={{ url: previewUrl, id: `${VIDEO_ID}-preview`, title }}
              previewVideo={{ url: previewUrl }}
            >
              <MediaPlayer.Root>
                <MediaPlayer.PreviewVideo />
              </MediaPlayer.Root>
            </MediaPlayer.Context>
          </span>
        )}
        <span className="z-1 relative flex items-center gap-2 rounded-full bg-white/5 px-4 py-2.5 text-white shadow-[0_9.841px_82.665px_0_rgba(0,0,0,0.75)] backdrop-blur-3xl transition-transform group-hover:scale-105">
          <MediaPlayer.PlayIcon aria-hidden className="w-3" />
          <span className="text-label-lg">
            <Trans>Play</Trans>
          </span>
        </span>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Viewport>
          <Dialog.Popup
            aria-label={title}
            className="bg-off-black w-[min(64rem,calc(100vw-2rem))]"
            closeButton={
              <Dialog.Close
                aria-label={t`Close`}
                className="text-button-sm absolute right-4 top-4 z-10 cursor-pointer text-white underline"
              >
                {t`Close`}
              </Dialog.Close>
            }
          >
            <div className="relative aspect-video w-full">
              <MediaPlayer.Context
                video={{
                  url: wistia(video.wistiaId),
                  id: VIDEO_ID,
                  title,
                  captions,
                  captionsDefault: !!captions,
                }}
              >
                <MediaPlayer.Root>
                  <MediaPlayer.Video
                    autoplay
                    playsInline
                    analyticsId="evt_home_fin4_video"
                  />
                  <MediaPlayer.Overlay playLabel={t`Play`} />
                  <MediaPlayer.Controls
                    controlsToShow={{ captions: !!captions }}
                  />
                  <MediaPlayer.SettingsMenu
                    labels={{
                      speed: t`Speed`,
                      quality: t`Quality`,
                      auto: t`Auto`,
                    }}
                  />
                </MediaPlayer.Root>
              </MediaPlayer.Context>
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
